import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    makeStyles
} from '@material-ui/core'
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles'
import {
    AtlasBlackNear,
    AtlasDataviz_02,
    AtlasGreyLight,
    ColorBaseMulberryBlue,
    ColorEastBay,
    ColorBaseDarkGray,
    AtlasGreyDark
} from 'assets/styles/variables'

import * as React from 'react'

import { FC, useRef, useEffect, useState } from 'react'
import { CustomSortIcon } from 'assets/icons/CustomSortIcon'
import TemplatesCardOverflow from './minutesTemplatePopoverButton'
import i18n from 'common/util/internationalization'
import TemplateDocumentIcon from 'assets/icons/TemplateDocumentIcon'
import { formatDate } from 'common/formatters/date'
import PreviewTemplateDrawer from 'pages/minutes-templates/preview-template-drawer'
import { fetchPreviewTemplatePDF } from 'businesslayer/networkrequests/templateService'
import { AtlasTooltip } from 'components/atlas-components/atlas-tooltip/Tooltip'
import { MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH } from 'common/constants/constants'

type Props = {
    // onClickOptions: (optionValue: OptionType, minutesId: any, committeeId: string) => void
    templateLists: any
    totalTemplates: any
    handlePageChange: any
    page: any
    handleSortBy: any
    reloadTemplateAfterDelete: any
    resetPaginationAndReload: () => void
    resetPaginationAndReloadWithCurrentSort: () => void
    setDefaultTemplateId: any
    downloadTemplateFile: any
    defaultTemplateId: any
    sortBy: string
    order: string
    setIsOpen: any
    isOpen: boolean
    systemTemplate?: any
    isLoading?: boolean
    setShowToast?: React.Dispatch<React.SetStateAction<boolean>>
    setToastType?: React.Dispatch<React.SetStateAction<string>>
    setToastMessage?: React.Dispatch<React.SetStateAction<string>>
}

const useStyles = makeStyles((theme) => ({
    tableRow: {
        '& > *': {
            borderBottom: 'none'
        },
        borderRadius: '10px',
        outline: `1px solid ${AtlasGreyLight}`,
        textAlign: 'left'
    },
    tableRowWhenGenerating: {
        '& > *': {
            borderBottom: 'none'
        },
        borderBottom: 'none',
        borderBottomLeftRadius: '10px',
        borderBottomRightRadius: '10px',
        outline: `1px solid ${AtlasGreyLight}`,
        textAlign: 'left'
    },
    tableGeneratingLabeRow: {
        borderTopLeftRadius: '10px',
        borderTopRightRadius: '10px',
        background: '#F5F8F9',
        outline: `1px solid ${AtlasGreyLight}`,
        textAlign: 'left',
        width: '100%'
    },
    tableContainer: {
        padding: '20px',
        gap: '24px',
        alignItems: 'center',
        overflowX: 'auto',
        width: '100%',
        [theme.breakpoints.down('md')]: {
            overflowX: 'auto',
            background: 'white',
            width: '100%'
        },
        '&::-webkit-scrollbar': {
            width: '6px'
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#acb1b8',
            borderRadius: '4px'
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
            borderRadius: '4px'
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: '#acb1b8'
        }
    },
    tableHead: (props: { isAdmin: boolean }) => ({
        color: `${AtlasBlackNear}`,
        fontSize: '16px',
        fontWeight: 600,
        lineHeight: '20px',
        width: 'auto',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        paddingLeft: '10px',
        cursor: 'pointer',
        // First column (Name) - needs more space for title + description + icon
        '&:nth-child(1)': {
            width: '450px',
            maxWidth: '450px'
        },
        // Second column (Uploaded By) - needs reasonable space for names
        '&:nth-child(2)': {
            width: '200px'
        },
        // Third column (Uploaded Date) - includes default tag, needs space
        '&:nth-child(3)': {
            width: '200px'
        },
        // Last column (Options) - compact but functional
        '&:nth-last-child(1)': props.isAdmin
            ? {
                  width: '100px',
                  textAlign: 'right',
                  cursor: 'default',
                  '& div': {
                      float: 'right'
                  }
              }
            : {},
        // Medium screens (tablets)
        [theme.breakpoints.down('md')]: {
            '&:nth-child(1)': {
                width: '250px !important'
            },
            '&:nth-child(2)': {
                width: '120px !important'
            },
            '&:nth-child(3)': {
                width: '120px !important'
            },
            '&:nth-last-child(1)': props.isAdmin
                ? {
                      width: '80px !important'
                  }
                : {}
        },
        // Smaller desktop screens
        '@media (max-width: 1367px)': {
            '&:nth-child(1)': {
                width: '320px !important'
            },
            '&:nth-child(2)': {
                width: '150px !important'
            },
            '&:nth-child(3)': {
                width: '150px !important'
            }
        }
    }),
    tableHead1: {
        display: 'flex',
        alignItems: 'center',
        '&:focus, &:active': {
            outline: 'none',
            boxShadow: `0px 0px 0px 1px #ffffff, 0px 0px 0px 3px ${ColorBaseMulberryBlue}`,
            borderRadius: '2px'
        }
    },
    titleHyperlink: {
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        textDecoration: 'none',
        textTransform: 'none',
        color: `${ColorEastBay}`,
        fontWeight: 600,
        lineHeight: '18px',
        textAlign: 'left',
        '&:focus': {
            outline: 'none',
            boxShadow: `0px 0px 0px 1px #ffffff, 0px 0px 0px 3px ${ColorBaseMulberryBlue}`,
            borderRadius: '2px'
        }
    },
    openBtn: {
        textDecoration: 'none',
        textTransform: 'none',
        color: `${ColorEastBay}`,
        fontWeight: 600,
        lineHeight: '18px',
        '&:focus': {
            outline: 'none',
            boxShadow: `0px 0px 0px 1px #ffffff, 0px 0px 0px 3px ${ColorBaseMulberryBlue}`,
            borderRadius: '2px'
        }
    },
    openBtnRight: {
        textTransform: 'none',
        color: `${ColorEastBay}`,
        fontWeight: 600,
        lineHeight: '18px',
        float: 'right'
    },
    rowData: {
        color: `${AtlasBlackNear}`,
        fontSize: '16px',
        fontWeight: 600,
        lineHeight: '18px',
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: '16px',
        paddingRight: '16px',
        columnGap: '1rem',
        justifyContent: 'left',
        width: '100%',
        whiteSpace: 'normal',
        overflow: 'hidden',
        borderBottom: 'none',
        textOverflow: 'ellipsis',
        wordBreak: 'break-word',
        '&:nth-last-child(1)': {
            width: '100px',
            textAlign: 'right'
        }
    },
    rowDataCommitteeName: {
        fontWeight: 400
    },
    statusColumn: {
        display: 'grid',
        gridTemplateColumns: '1.2rem 3.9rem',
        justifyContent: 'left',
        alignItems: 'center',
        fontSize: '14px'
    },
    inReviewBox: {
        background: `${AtlasDataviz_02}`,
        height: '12px',
        maxWidth: '12px',
        borderRadius: '2px'
    },
    inDraftBox: {
        background: `#FFB400`,
        height: '12px',
        maxWidth: '12px',
        borderRadius: '2px'
    },
    finalDraftBox: {
        background: `#D64933`,
        height: '12px',
        maxWidth: '12px',
        borderRadius: '2px'
    },
    approvedBox: {
        background: `#3E8914`,
        height: '12px',
        maxWidth: '12px',
        borderRadius: '2px'
    },
    showMoreBtn: {
        textTransform: 'none',
        color: `${ColorEastBay}`,
        fontWeight: 600,
        lineHeight: '20px',
        border: `1px solid ${ColorEastBay}`,
        padding: '8px 12px 8px 12px',
        maxWidth: '169px',
        display: 'block',
        margin: 'auto',
        '&:focus': {
            outline: 'none',
            boxShadow: `0px 0px 0px 1px #ffffff, 0px 0px 0px 3px ${ColorBaseMulberryBlue}`,
            borderRadius: '2px'
        }
    },
    showMoreBtnLoading: {
        textTransform: 'none',
        color: `${ColorEastBay}`,
        fontWeight: 600,
        lineHeight: '20px',
        border: `1px solid ${ColorEastBay}`,
        padding: '8px 12px 8px 12px',
        maxWidth: '169px',
        display: 'block',
        margin: 'auto',
        backgroundColor: 'white !important',
        '&:disabled': {
            backgroundColor: 'white !important',
            color: `${ColorEastBay} !important`,
            border: `1px solid ${ColorEastBay} !important`
        },
        '&:focus': {
            outline: 'none',
            boxShadow: `0px 0px 0px 1px #ffffff, 0px 0px 0px 3px ${ColorBaseMulberryBlue}`,
            borderRadius: '2px'
        }
    },
    showMoreBtnContainer: {
        padding: '0px 0 10px 0',
        // iPad specific bottom padding
        '@media (min-width: 768px) and (max-width: 1024px)': {
            padding: '0px 0 48px 0'
        }
    },
    dueDateLabel: {
        fontWeight: 400,
        display: 'flex',
        gap: '1rem',
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    overDueIcon: {
        fontWeight: 600,
        display: 'flex',
        gap: '0.5rem',
        color: '#935206'
    },
    minutesGenerationStatusContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignSelf: 'center',
        alignItems: 'center'
    },
    generatingLabel: {
        fontFamily: 'Source Sans Pro',
        fontSize: '14px',
        marginTop: '3px',
        paddingLeft: '4px',
        fontWeight: 600,
        color: `${ColorBaseDarkGray}`,
        lineHeight: '18px'
    },
    defaultTag: {
        backgroundColor: '#E6E6E6',
        padding: '5px 10px',
        textAlign: 'center',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 400,
        color: '#1E1E1E',
        marginRight: theme.spacing(2),
        marginLeft: '60px'
    },
    templateDocumentIconAlignment: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 50,
        width: 50,
        minWidth: 50,
        minHeight: 50,
        marginRight: 8
    },
    subtitle: {
        color: AtlasGreyDark,
        fontSize: '16px',
        fontWeight: 400
    },
    templateDescription: {
        fontFamily: 'Source Sans Pro'
    },
    templateTitleWithDescription: {
        display: 'flex',
        flexDirection: 'column'
    }
}))

const EnhancedTableHead = (props) => {
    const { order, sortBy, handleSortBy, isAdmin } = props
    const classes = useStyles({ isAdmin })

    const formatLabelForAnalytics = (label) => {
        return label
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join('')
    }
    const handleSortOnClick = React.useCallback(
        (id) => {
            const mode = order === 'asc' && sortBy === id ? 'desc' : 'asc'
            if (id === 'options') return
            handleSortBy(id, mode)
        },
        [handleSortBy, order, sortBy]
    )

    enum HeadCellID {
        Name = 'title',
        Uploaded_By = 'createdBy',
        Uploaded_Date = 'createdAt',
        UpdatedAt = 'updated_at',
        Options = 'options'
    }

    interface HeadCell {
        id: HeadCellID
        label: string
    }

    // Main function
    const headCells = (canAddMinutes: boolean): HeadCell[] => {
        const commonItems: HeadCell[] = [
            { id: HeadCellID.Name, label: i18n.t('MINUTES_TEMPLATES_NAME') },
            { id: HeadCellID.Uploaded_By, label: i18n.t('MINUTES_TEMPLATES_UPLOADED_BY') },
            { id: HeadCellID.Uploaded_Date, label: i18n.t('MINUTES_TEMPLATES_UPLOADED_DATE') }
        ]

        // Conditional items
        const additionalItems: HeadCell[] = canAddMinutes
            ? [{ id: HeadCellID.Options, label: '' }]
            : []

        return [...commonItems, ...additionalItems]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const mapHeaderCell = React.useMemo(() => headCells(isAdmin) ?? [], [isAdmin])

    return (
        <TableHead>
            <TableRow>
                {mapHeaderCell.map((headCell) =>
                    headCell.id.length > 0 ? (
                        <TableCell
                            data-analytics={`MM-Sort-${formatLabelForAnalytics(headCell.label)}`}
                            key={headCell.id}
                            align={headCell.id !== 'title' ? 'left' : 'left'}
                            style={{ borderBottom: 'none' }}
                            className={`${classes.tableHead}`}
                            sortDirection={sortBy === headCell.id ? order : false}
                            role="columnheader">
                            <Box
                                className={classes.tableHead1}
                                onClick={() => handleSortOnClick(headCell.id)}
                                aria-label={headCell.label}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        handleSortOnClick(headCell.id)
                                    }
                                }}
                                aria-sort={
                                    sortBy === headCell.id
                                        ? order === 'asc'
                                            ? 'ascending'
                                            : 'descending'
                                        : 'none'
                                }
                                {...(headCell.label && { tabIndex: 0 })}>
                                {headCell.label}
                                {headCell.id && sortBy === headCell.id && (
                                    <IconButton
                                        tabIndex={-1}
                                        aria-label={` ${
                                            order === 'asc' ? 'ascending' : 'descending'
                                        }`}>
                                        <CustomSortIcon
                                            isAscending={order === 'asc'}
                                            selected={sortBy === headCell.id}
                                        />
                                    </IconButton>
                                )}
                            </Box>
                        </TableCell>
                    ) : (
                        <td></td>
                    )
                )}
            </TableRow>
        </TableHead>
    )
}

const MinutesTemplatesComponent: FC<Props> = ({
    handleSortBy,
    sortBy,
    order,
    templateLists,
    totalTemplates,
    handlePageChange,
    page,
    setIsOpen,
    isOpen,
    setDefaultTemplateId,
    downloadTemplateFile,
    defaultTemplateId,
    reloadTemplateAfterDelete,
    resetPaginationAndReload,
    resetPaginationAndReloadWithCurrentSort,
    systemTemplate,
    isLoading = false,
    setShowToast,
    setToastType,
    setToastMessage
}) => {
    const isAdmin = true // or derive this value from props or state
    const classes = useStyles({ isAdmin })
    const tableContainerRef = useRef<HTMLDivElement | null>(null)
    const [isPreviewDrawerOpen, setIsPreviewDrawerOpen] = useState(false)
    const [imageUrls, setImageUrls] = useState<string[]>([])
    const [pageCount, setPageCount] = useState<number>(0)
    const [loading, setLoading] = useState(false)
    const [currentPreviewedTemplate, setCurrentPreviewedTemplate] = useState<any>(null)
    const truncateTitle = (title, limit = 366) =>
        title?.length > limit ? `${title.slice(0, limit)}...` : title

    const renderTitleLink = (title, templateList) => {
        return (
            <div
                id="ContentContainer_ContentBody"
                data-analytics="MM-Template-Preview"
                tabIndex={0}
                onClick={() => handleGetTemplatePreview(templateList)}
                onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault()
                        handleGetTemplatePreview(templateList)
                    }
                }}
                className={classes.titleHyperlink}>
                {truncateTitle(title, MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH)}
            </div>
        )
    }

    // Scroll to top on default template change
    useEffect(() => {
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }, [defaultTemplateId])

    const handleGetTemplatePreview = async (templateList: any) => {
        setIsPreviewDrawerOpen(true)
        setCurrentPreviewedTemplate(templateList)
        try {
            setLoading(true)
            const response = await fetchPreviewTemplatePDF(templateList.id)
            if (response?.success && response?.images && response?.images?.length > 0) {
                setImageUrls(response?.images)
                setPageCount(response?.pageCount || response?.images?.length)
            } else {
                setImageUrls([])
                setPageCount(0)
                console.error(response?.error)
            }
        } catch (err) {
            setImageUrls([])
            setPageCount(0)
            console.error(err)
        } finally {
            setLoading(false)
        }
    }
    /* Customized Tooltip theme */
    const theme = createMuiTheme({
        overrides: {
            MuiTooltip: {
                tooltip: {
                    fontFamily: 'Source Sans Pro'
                }
            }
        }
    })
    return (
        <>
            <style>{`
        .hide-scrollbar {
          overflow-y: auto;
          scrollbar-width: none;        /* Firefox */
          -ms-overflow-style: none;     /* IE/Edge */
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;                /* Chrome/Safari */
        }
      `}</style>
            <TableContainer
                ref={tableContainerRef}
                className={`${classes.tableContainer} hide-scrollbar`}
                style={{
                    maxHeight: 'calc(-300px + 58.5vh)',
                    overflow: 'visible'
                }}>
                <Table
                    style={{
                        borderSpacing: '0 10px',
                        tableLayout: 'fixed',
                        maxWidth: '100%',
                        marginBottom: '7px'
                    }}>
                    <EnhancedTableHead
                        handleSortBy={handleSortBy}
                        sortBy={sortBy}
                        order={order}
                        isAdmin={isAdmin}
                    />
                    <TableBody>
                        {templateLists &&
                            templateLists?.length !== 0 &&
                            templateLists.map((templateList, index) => (
                                <React.Fragment key={index}>
                                    <TableRow className={classes.tableRow}>
                                        <TableCell
                                            data-analytics="MM-Template-Preview"
                                            component="th"
                                            scope="row"
                                            align="left"
                                            padding="default"
                                            className={classes.rowData}
                                            role="cell">
                                            <ThemeProvider theme={theme}>
                                                <span
                                                    className={
                                                        classes.minutesGenerationStatusContainer
                                                    }
                                                    data-analytics="MM-Template-Preview"
                                                    style={{ alignItems: 'center' }}>
                                                    <span
                                                        className={`${classes.templateDocumentIconAlignment}`}>
                                                        <TemplateDocumentIcon />
                                                    </span>
                                                    <div
                                                        className={
                                                            classes.templateTitleWithDescription
                                                        }>
                                                        <AtlasTooltip
                                                            title={
                                                                templateList?.title.length >
                                                                MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH
                                                                    ? templateList?.title
                                                                    : ''
                                                            }
                                                            interactive
                                                            placement="top">
                                                            {renderTitleLink(
                                                                templateList.title,
                                                                templateList
                                                            )}
                                                        </AtlasTooltip>
                                                        <AtlasTooltip
                                                            title={
                                                                MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH
                                                                    ? templateList.description
                                                                    : ''
                                                            }
                                                            interactive
                                                            placement="top">
                                                            <Typography
                                                                className={classes.subtitle}>
                                                                <div
                                                                    className={
                                                                        classes.templateDescription
                                                                    }>
                                                                    {truncateTitle(
                                                                        templateList?.description,
                                                                        MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH
                                                                    )}
                                                                </div>
                                                            </Typography>
                                                        </AtlasTooltip>
                                                    </div>
                                                </span>
                                            </ThemeProvider>
                                        </TableCell>
                                        <TableCell
                                            className={`${classes.rowDataCommitteeName}${classes.rowData}`}
                                            padding="default"
                                            align="left"
                                            role="cell">
                                            {templateList.displayCreatedBy}
                                        </TableCell>
                                        <TableCell
                                            padding="default"
                                            className={`${classes.rowDataCommitteeName}${classes.rowData}`}
                                            align="left"
                                            role="cell">
                                            {formatDate({ date: templateList.createdAt })}

                                            {templateList?.isDefault && (
                                                <span className={classes.defaultTag}>
                                                    {i18n.t('MINUTES_TEMPLATES_DEFAULT_TEMPLATE')}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell
                                            padding="default"
                                            className={classes.rowData}
                                            align="left"
                                            role="cell">
                                            <Box>
                                                <TemplatesCardOverflow
                                                    templateList={templateList}
                                                    reloadTemplateAfterDelete={
                                                        reloadTemplateAfterDelete
                                                    }
                                                    resetPaginationAndReload={
                                                        resetPaginationAndReload
                                                    }
                                                    resetPaginationAndReloadWithCurrentSort={
                                                        resetPaginationAndReloadWithCurrentSort
                                                    }
                                                    setDefaultTemplateId={setDefaultTemplateId}
                                                    defaultTemplateId={defaultTemplateId}
                                                    downloadTemplateFile={downloadTemplateFile}
                                                    data-testid="MinutesManager_MinutesOptions_Btn"
                                                    onClickOptions={() => {
                                                        // console.log(e)
                                                    }}
                                                    setIsOpen={setIsOpen}
                                                    isOpen={isOpen}
                                                    setShowToast={setShowToast}
                                                    setToastType={setToastType}
                                                    setToastMessage={setToastMessage}
                                                />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    <tr style={{ height: '10px' }}></tr>
                                </React.Fragment>
                            ))}
                    </TableBody>
                </Table>
                <PreviewTemplateDrawer
                    imageUrlsData={imageUrls}
                    pageCount={pageCount}
                    loading={loading}
                    setIsOpen={setIsPreviewDrawerOpen}
                    isOpen={isPreviewDrawerOpen}
                    downloadTemplateFile={downloadTemplateFile}
                    currentTemplate={currentPreviewedTemplate}
                    systemTemplate={systemTemplate}
                />
                {totalTemplates > templateLists?.length && (
                    <Box className={classes.showMoreBtnContainer}>
                        <Button
                            disableFocusRipple
                            disableRipple
                            disabled={isLoading}
                            onClick={() => handlePageChange(page + 1)}
                            data-testid="ShowMore_Btn"
                            aria-label={i18n.t('MINUTES_TEMPLATES_SHOW_MORE_TEMPLATES') as string}
                            data-analytics="MM-ShowMoreTemplates"
                            className={isLoading ? classes.showMoreBtnLoading : classes.showMoreBtn}
                            style={isLoading ? { backgroundColor: 'white' } : {}}>
                            {isLoading ? (
                                <CircularProgress
                                    size={24}
                                    thickness={4}
                                    style={{ color: ColorEastBay }}
                                />
                            ) : (
                                i18n.t('MINUTES_TEMPLATES_SHOW_MORE_TEMPLATES')
                            )}
                        </Button>
                    </Box>
                )}
            </TableContainer>
        </>
    )
}

export default MinutesTemplatesComponent
